import { trackEvent } from '@/api/track';
import { useStudioStore } from '@/store/useStudioStore';

export type StudioTrackingData = Record<string, unknown>;

export interface StudioExpertMessageTrackingMeta {
  requestType?: string;
  hasSelectedElement?: boolean;
  hasEditedFields?: boolean;
  isExpertReviewActive?: boolean;
  hasAttachment?: boolean;
  startedAt?: number;
}

export const getStudioTrackingContext = () => {
  const state = useStudioStore.getState();
  const sitePath = state.currentProject?.sitePaths?.[0];
  const projectTypeId =
    state.currentProjectTypeId ??
    state.currentProject?.projectTypeId ??
    state.currentProject?.projectType;
  const siteId = state.currentSiteId ?? (sitePath?.siteId ? String(sitePath.siteId) : undefined);
  const isPublished = Boolean(state.lastPublishedUrl || siteId || sitePath?.path);

  return {
    projectId: state.currentProjectId ?? undefined,
    userProjectId: state.currentId ?? undefined,
    siteId,
    projectTypeId: projectTypeId ?? undefined,
    isDomainBound: state.currentSiteInfo ? state.currentSiteInfo.status === 2 : false,
    isGadsTagConnected: state.isGadsTagConnected,
    isPublished,
  };
};

export const trackStudioEvent = (eventCode: string, eventData: StudioTrackingData = {}) => {
  try {
    trackEvent({
      eventCode,
      eventData: JSON.stringify({
        ...getStudioTrackingContext(),
        ...eventData,
      }),
    });
  } catch {
    // Studio tracking failures should not affect editing or publishing flows.
  }
};

const normalizeChangedField = (key: string) => {
  if (key === 'src') return 'image';
  if (key === 'href' || key === 'hrefItems') return 'link';
  if (key === 'text' || key === 'textItems') return 'text';
  return key;
};

export const getStudioChangedFields = (updates: object) =>
  Array.from(
    new Set(
      Object.entries(updates)
        .filter(([, value]) => value !== undefined)
        .map(([key]) => normalizeChangedField(key)),
    ),
  );

export const getStudioExpertRequestType = ({
  isExpertReviewActive,
  hasSelectedElement,
  hasEditedFields,
}: {
  isExpertReviewActive?: boolean;
  hasSelectedElement?: boolean;
  hasEditedFields?: boolean;
}) => {
  if (isExpertReviewActive) return 'expert_review';
  if (hasSelectedElement && hasEditedFields) return 'selected_element_edit';
  if (hasSelectedElement) return 'selected_element_prompt';
  return 'chat_edit';
};

export const getStudioExpertMessageDurationMs = (
  trackingMeta?: StudioExpertMessageTrackingMeta,
) => {
  if (typeof trackingMeta?.startedAt !== 'number') return undefined;
  return Math.max(0, Date.now() - trackingMeta.startedAt);
};

export const trackStudioExpertMessageSubmit = (
  trackingMeta: StudioExpertMessageTrackingMeta,
  eventData: StudioTrackingData = {},
) => {
  trackStudioEvent('studio_expert_message_submit', {
    requestType: trackingMeta.requestType,
    hasSelectedElement: trackingMeta.hasSelectedElement,
    hasEditedFields: trackingMeta.hasEditedFields,
    isExpertReviewActive: trackingMeta.isExpertReviewActive,
    hasAttachment: trackingMeta.hasAttachment ?? false,
    ...eventData,
  });
};

export const trackStudioExpertMessageResult = (
  trackingMeta: StudioExpertMessageTrackingMeta | undefined,
  eventData: StudioTrackingData = {},
) => {
  trackStudioEvent('studio_expert_message_result', {
    requestType: trackingMeta?.requestType,
    durationMs: getStudioExpertMessageDurationMs(trackingMeta),
    ...eventData,
  });
};
